/*
 * ApplicationDetailsLegalDocuments.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Snackbar,
  Container,
  Typography,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import { fetchLegalDocsDetails } from "../../Services/redux/LegalDocuments/LegalDocumentsActions";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddIcon from "@mui/icons-material/Add";
import "./ApplicationDetailsLegalDoucments.css";
import { getConfig } from "../../config";

function ApplicationDetailsLegalDocuments({ postCallTheNewSubmission }) {
  const dispatch = useDispatch();
  const [legalServiceId, setLegalServiceId] = useState("");
  const [legalDocumentsArray, setLegalDocumentsArray] = useState([]);
  const [showLegalDocumentary, setShowLegalDocumentary] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [errorLegalServiceId, setErrorLegalServiceId] = useState(false);
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  const legalDocuments = useSelector(
    (state) => state.legalDocumentDetails.legaldocuments
  );
  const [showEditTable, setShowEditTable] = useState(false);
  const portalType = getConfig().REACT_APP_PORTAL;
  const hasSubmissionData =
    !!submissionData && Object.keys(submissionData).length > 0;
  const isSellerOrDomainApprover =
    portalType === "seller" || portalType === "domain-approver";
  const isOemAdmin = portalType === "oem-admin";
  const isDraftSubmission = submissionData?.status === "DRAFT";
  const rowKeyMapRef = useRef(new WeakMap());
  const rowKeyCounterRef = useRef(0);

  const getRowKey = (row, prefix) => {
    if (!row || typeof row !== "object") {
      return `${prefix}-fallback`;
    }

    if (!rowKeyMapRef.current.has(row)) {
      rowKeyCounterRef.current += 1;
      rowKeyMapRef.current.set(row, `${prefix}-${rowKeyCounterRef.current}`);
    }

    return rowKeyMapRef.current.get(row);
  };

  const syncSellerOrDomainApproverData = useCallback((associatedServiceId) => {
    const legalData = submissionData?.legalDocuments;

    if (legalData) {
      const legalarray = legalData?.legalDocumentInfos?.["en_US"] || [];
      setLegalServiceId(legalData.legalServiceId || associatedServiceId);
      setLegalDocumentsArray([...legalarray]);
      setShowLegalDocumentary([...legalarray]);
      setErrorLegalServiceId(false);
      return;
    }

    if (associatedServiceId) {
      setLegalServiceId(associatedServiceId);
    }
  }, [submissionData]);

  const syncOemAdminData = useCallback((associatedServiceId) => {
    const legalData = submissionData?.legalDocument;

    if (legalData) {
      const legalarray = legalData?.legalDocumentInfos?.["en_US"] || [];
      setLegalServiceId(legalData.legalServiceId || associatedServiceId);
      setLegalDocumentsArray([...legalarray]);
      setShowLegalDocumentary([...legalarray]);
      return;
    }

    setLegalDocumentsArray([]);
    setShowLegalDocumentary([]);
    if (associatedServiceId) {
      setLegalServiceId(associatedServiceId);
    }
  }, [submissionData]);

  const addLegalDocumentArray = () => {
    const obj = {
      type: "",
      documentId: "",
    };
    const showLegalArray = [...showLegalDocumentary];
    showLegalArray.push(obj);
    setShowLegalDocumentary([...showLegalArray]);
  };

  useEffect(() => {
    if (portalType === "seller") {
      dispatch(fetchLegalDocsDetails());
    }
  }, [dispatch, portalType]);

  useEffect(() => {
    if (!hasSubmissionData) {
      return;
    }

    const associatedServiceId = submissionData?.associatedServiceIds?.join("") || "";

    if (isSellerOrDomainApprover) {
      syncSellerOrDomainApproverData(associatedServiceId);
      return;
    }

    if (isOemAdmin) {
      syncOemAdminData(associatedServiceId);
    }
  }, [
    hasSubmissionData,
    isSellerOrDomainApprover,
    isOemAdmin,
    legalDocuments,
    syncOemAdminData,
    syncSellerOrDomainApproverData,
    submissionData,
  ]);

  const changeLegalId = (event) => {
    if (event.target.value === "") {
      setLegalServiceId(event.target.value);
      setErrorLegalServiceId(true);
    } else {
      setLegalServiceId(event.target.value);
      setErrorLegalServiceId(false);
    }
  };

  const changeType = (event, index) => {
    const selectedType = event.target.value;
    const isDuplicate = showLegalDocumentary.some(
      (doc, idx) => doc.type === selectedType && idx !== index
    );
    if (isDuplicate) {
      setOpenSnackbar(true);
      setSnackMsg(
        "This document type is already selected. Please choose a different type."
      );
      return;
    }
    const newlegalArray = [...showLegalDocumentary];
    newlegalArray[index].type = event.target.value;
    setShowLegalDocumentary([...newlegalArray]);
  };

  const changeDocumentId = (event, index) => {
    const selectedId = event.target.value;
    const isDuplicate = showLegalDocumentary.some(
      (doc, idx) =>
        doc.documentId === selectedId && idx !== index && doc.documentId !== ""
    );
    if (isDuplicate) {
      setOpenSnackbar(true);
      setSnackMsg(
        "This document ID is already used. Please enter a unique document ID."
      );
      return;
    }
    const newlegalArray = [...showLegalDocumentary];
    newlegalArray[index].documentId = event.target.value;
    setShowLegalDocumentary([...newlegalArray]);
  };

  const saveLegalDocuments = () => {
    const hasEmptyFields = showLegalDocumentary.some(
      (doc) => !doc.type || !doc.documentId
    );
    if (hasEmptyFields) {
      setOpenSnackbar(true);
      setSnackMsg(
        "Please fill in both Document Type and Document ID for all documents."
      );
      return;
    }
    const obj = {
      legalDocuments: {
        language: "en_US",
        legalServiceId: legalServiceId,
        legalDocumentTypeInfo: [...showLegalDocumentary],
      },
    };
    if (legalServiceId === "") {
      setErrorLegalServiceId(true);
      return;
    }

    const newSubmissionData = { ...submissionData };
    newSubmissionData.legalDocuments = obj.legalDocuments;
    postCallTheNewSubmission(newSubmissionData);
    setOpenSnackbar(true);
    setSnackMsg(`Legal documents saved`);
  };

  const handleCancel = () => {
    setShowEditTable(false);
    setShowLegalDocumentary([...legalDocumentsArray]);
    setLegalServiceId(
      submissionData?.legalDocuments?.legalServiceId ||
        submissionData?.associatedServiceIds?.join("") ||
        ""
    );
    setErrorLegalServiceId(false);
  };

  const handleDelete = (event, index) => {
    event.stopPropagation();
    const newLegalArray = [...showLegalDocumentary];
    newLegalArray.splice(index, 1);
    setShowLegalDocumentary([...newLegalArray]);
  };

  const showEditTableClick = () => {
    setShowEditTable(true);
  };

  const renderLegalDocumentsTable = () => {
    if (hasSubmissionData && !isDraftSubmission) {
      if (legalDocumentsArray && legalDocumentsArray.length > 0) {
        return (
                  <TableContainer sx={{ marginTop: "20px" }}>
                    <Table
                      sx={{
                        borderSpacing: 0,
                        borderBottom: "1px solid #12141533",
                      }}
                    >
                      {" "}
                      <TableHead sx={{ borderSpacing: 0 }}>
                        <TableRow
                          sx={{
                            backgroundColor: "#E8E8E8",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              fontWeight: "550",
                              textAlign: "left",
                              border: "1px solid #12141533",
                            }}
                          >
                            Document Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              fontWeight: "550",
                              textAlign: "left",
                              border: "1px solid #12141533",
                            }}
                          >
                            Document Id
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {legalDocumentsArray.map((row) => {
                          return (
                            <TableRow key={getRowKey(row, "readonly-row")}>
                              <TableCell
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                }}
                                style={{
                                  border: "1px solid  #12141533",
                                  borderBottom: "0px",
                                }}
                              >
                                {row.type}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                }}
                                style={{
                                  border: "1px solid #12141533",
                                  borderBottom: "0px",
                                }}
                              >
                                {row.documentId}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow></TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
        );
      }

      return (
                  <TableContainer sx={{ marginTop: "20px" }}>
                    <Table
                      sx={{
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                      {" "}
                      <TableHead
                        sx={{
                          borderCollapse: "separate",
                          borderSpacing: 0,
                        }}
                      >
                        <TableRow
                          sx={{
                            backgroundColor: "#E8E8E8",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              fontWeight: "550",
                              textAlign: "left",
                              border: "1px solid #12141533",
                            }}
                          >
                            Document Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              fontWeight: "550",
                              textAlign: "left",
                              border: "1px solid #12141533",
                            }}
                          >
                            Document Id
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#2A2C2D",
                              textAlign: "center !important",
                              alignContent: "center !important",
                              paddingTop: "10px !important",
                              paddingBottom: "10px !important",
                            }}
                            className="uploadSignedApkText"
                          >
                            No legal documents are added
                          </Typography>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
      );
    }

    if (!isDraftSubmission) {
      return null;
    }

    return (
                  <TableContainer sx={{ marginTop: "20px" }}>
                    <Table>
                      {" "}
                      <TableHead
                        sx={{
                          borderCollapse: "separate",
                          borderSpacing: 0,
                        }}
                      >
                        <TableRow
                          sx={{
                            backgroundColor: "#E8E8E8",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              fontWeight: "550",
                              textAlign: "left",
                              border: "1px solid #12141533",
                              padding: "8px !important",
                            }}
                          >
                            Document Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              fontWeight: "550",
                              textAlign: "left",
                              border: "1px solid #12141533",
                              padding: "8px !important",
                            }}
                          >
                            Document Id
                          </TableCell>
                          {showEditTable && showLegalDocumentary.length > 0 && (
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                fontWeight: "550",
                                lineHeight: "16px",
                                letterSpacing: "0.4000000059604645px",
                                textAlign: "left",
                                border: "1px solid #12141533",
                              }}
                            >
                              Action
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      {showLegalDocumentary &&
                        showLegalDocumentary.length > 0 &&
                        showLegalDocumentary.map((legalDoc, index) => {
                          return (
                            <TableRow key={getRowKey(legalDoc, "editable-row")}>
                              <TableCell>
                                <Select
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    lineHeight: "24px",
                                    letterSpacing: "0.10000000149011612px",
                                    textAlign: "left",
                                    color: "#2A2C2D",
                                    width: "100%",
                                    padding: "0px",
                                    marginTop: "0.9px !important",
                                    border: "none",
                                  }}
                                  disabled={!showEditTable}
                                  className="input-field"
                                  labelId="demo-multiple-name-label"
                                  id="demo-multiple-name"
                                  variant="outlined"
                                  value={legalDoc.type}
                                  defaultValue="select a documentType"
                                  onChange={(event) =>
                                    changeType(event, index)
                                  }
                                >
                                  {legalDocuments.map((option) => (
                                    <MenuItem key={option.type} value={option.type}>
                                      {option.type}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  lineHeight: "24px",
                                  letterSpacing: "0.10000000149011612px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                }}
                                style={{ border: "1px solid #12141533" }}
                              >
                                <TextField
                                  fullWidth
                                  value={legalDoc.documentId}
                                  sx={{
                                    "font-size": "14px",
                                    "font-weight": 400,
                                    "line-height": "16.41px",
                                    "text-align": "left",
                                    width: "100%",
                                    color: "rgba(113, 114, 115, 1)",
                                  }}
                                  disabled={!showEditTable}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: false,
                                      style: {
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        lineHeight: "24px",
                                        letterSpacing: "0.10000000149011612px",
                                        textAlign: "left",
                                        color: "#2A2C2D",
                                        paddingLeft: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      },
                                    },
                                  }}
                                  label={!legalDoc.documentId && "document Id"}
                                  variant="outlined"
                                  onChange={(event) =>
                                    changeDocumentId(event, index)
                                  }
                                />
                              </TableCell>
                              {showEditTable &&
                                showLegalDocumentary.length > 0 && (
                                  <TableCell>
                                    <IconButton
                                      aria-label="Delete legal document"
                                      onClick={(event) => handleDelete(event, index)}
                                      disabled={!showEditTable}
                                      size="small"
                                      sx={{ padding: 0 }}
                                    >
                                      <RemoveCircleIcon
                                        sx={{
                                          color: "#B50B00 !important",
                                          opacity: showEditTable ? 1 : 0.5,
                                          cursor: showEditTable ? "pointer" : "default",
                                        }}
                                      />
                                    </IconButton>
                                  </TableCell>
                                )}
                            </TableRow>
                          );
                        })}
                      {(!showLegalDocumentary ||
                        showLegalDocumentary.length === 0) && (
                        <TableRow>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: "500",
                              lineHeight: "24px",
                              letterSpacing: "0.10000000149011612px",
                              color: "#2A2C2D",
                              textAlign: "center !important",
                              alignContent: "center !important",
                              paddingTop: "10px !important",
                              paddingBottom: "10px !important",
                            }}
                            className="uploadSignedApkText"
                          >
                            No legal documents are added
                          </Typography>
                        </TableRow>
                      )}
                    </Table>
                  </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex !important",
            width: "100%",
          }}
        >
          <div
            className="legal-documentation"
            id="legal-documentation-section"
            style={
              hasSubmissionData && !isDraftSubmission
                ? {
                    marginTop: "0px",
                  }
                : { marginTop: "30px", width: "85%" }
            }
          >
            <div className="text-button-row">
              <Typography
                sx={{
                  "font-weight": "bold !important",
                  "font-size": "20px !important",
                  // "letter-spacing": "0.4px !important",
                  color: "rgba(18, 20, 21, 1) !important",
                }}
                className="uploadSignedApkText"
              >
                Legal Documentation
              </Typography>
              {hasSubmissionData &&
                isDraftSubmission &&
                !showEditTable && (
                  <Button
                    sx={{
                      fontWeight: "400px !important",
                      fontSize: "12px !important",
                      letterSpacing: "0.4px !important",
                      lineHeight: "19.6px",
                      color: "rgba(4, 118, 226, 1) !important",
                      border: "1px solid rgba(4, 118, 226, 1) !important",
                      textTransform: "none",
                      height: "32px !important",
                      gap: "8px",
                    }}
                    onClick={showEditTableClick}
                  >
                    <AddIcon sx={{ marginLeft: "-5px" }} />
                    Manage Document
                  </Button>
                )}
            </div>
            <div>
              {
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "10px",
                  }}
                >
                  <TextField
                    slotProps={{
                      inputLabel: {
                        shrink: false,
                        style: {
                          "font-size": "14px",
                          "font-weight": 400,
                          "line-height": "16.41px",
                          "text-align": "left",
                          color: "rgba(113, 114, 115, 1)",
                          textAlign: "center",
                          padding: "5px !important",
                          paddingBottom: "5px !important",
                          top: "30%",
                          left: "6.5%",
                          transform: "translateY(-50%)",
                          position: "absolute",
                        },
                      },
                      formHelperText: {
                        style: {
                          marginTop: "4px",
                          marginLeft: "-1px",
                        },
                      },
                    }}
                    variant="outlined"
                    label={!legalServiceId && "Legal Service Id"}
                    value={legalServiceId}
                    disabled={!showEditTable}
                    onChange={(event) => changeLegalId(event)}
                    error={errorLegalServiceId}
                    helperText={
                      errorLegalServiceId ? "Please Add Legal ServiceId" : ""
                    }
                  />
                  {showEditTable && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {showLegalDocumentary &&
                        showLegalDocumentary.length < 3 &&
                        submissionData.status === "DRAFT" &&
                        showEditTable && (
                          <Button
                            sx={{
                              "font-size": "12px !important",
                              // "letter-spacing": "0.4px !important",
                              "line-height": "19.6px",
                              color: "rgba(4, 118, 226, 1) !important",
                              border:
                                "1px solid rgba(4, 118, 226, 1) !important",
                              textTransform: "none",
                              marginRight: "5px",
                              width: "150px",
                            }}
                            onClick={addLegalDocumentArray}
                          >
                            <AddIcon />
                            Add Document
                          </Button>
                        )}
                      <Button
                        className="cancelLegalDocumnet"
                        onClick={handleCancel}
                        sx={{
                          textTransform: "none",
                          marginRight: "5px",
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="savelegalDocument"
                        onClick={saveLegalDocuments}
                        sx={{
                          textTransform: "none",
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              }

              {renderLegalDocumentsTable()}
              <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                message={snackMsg}
              />
            </div>
          </div>
        </Box>
      </Box>
    </Container>
  );
}

export default ApplicationDetailsLegalDocuments;

ApplicationDetailsLegalDocuments.propTypes = {
  postCallTheNewSubmission: PropTypes.func.isRequired,
};
